import { useEffect, useMemo, useState } from 'react';
import { FileBarChart2, Clock, Route, Video, Filter, BarChart3, ShieldAlert, Receipt, Download } from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import { useDrivers } from '../../hooks/useDrivers';
import { useVehicles } from '../../hooks/useVehicles';
import { useTrips } from '../../hooks/useTrips';
import { useExpenses } from '../../hooks/useExpenses';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useLogbookEntries } from '../../hooks/useLogbookEntries';
import { useLogbookTypes } from '../../hooks/useLogbookTypes';
import { TripsTable } from '../../components/trips/TripsTable';
import { TripDetailModal } from '../../components/trips/TripDetailModal';
import {
  CATEGORY_LABELS, SEVERITY_LABELS, SEVERITY_COLORS,
  RISK_COLORS, RISK_LABELS, VEHICLE_STATUS_LABELS, VEHICLE_STATUS_COLORS,
} from '../../utils/constants';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/Button';
import { formatearCOP } from '../../utils/expenses';
import { exportToCsv } from '../../utils/csv';
import type { VehicleStatus } from '../../types/vehicle';
import type { RiskLevel } from '../../types/driver';

type ReportTab = 'dashboard' | 'events' | 'trips' | 'expenses' | 'video';

// FE-09 (parcial, 2026-09-01): vehículos/alertas/conductores pasan a leer de
// los hooks reales (mismo origen que FE-04).
//
// FE-11 (2026-09-11): la pestaña de VIAJES sale de "No disponible" — `/trips`
// ya existe y responde. Queda un solo panel vacío, el de video histórico, que
// sigue sin ningún contrato ni épica de backend asignada. El criterio no ha
// cambiado: se muestra vacío antes que inventar datos.
function UnavailablePanel({ title, reason }: { title: string; reason: string }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)' }}>
      <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</p>
      <p style={{ margin: '0.5rem 0 0', fontSize: '13px' }}>{reason}</p>
    </div>
  );
}

export default function ReportsPage() {
  useEffect(() => { document.title = 'Reportes Ejecutivos | Paitron Radar'; }, []);

  const { alerts } = useAlerts();
  const { drivers, isLoading: driversLoading } = useDrivers();
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();
  const isLoading = driversLoading || vehiclesLoading;

  const [tab, setTab] = useState<ReportTab>('dashboard');
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterDriver, setFilterDriver] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  // issue #124: filtros propios de la pestaña Gastos y Novedades — categoría
  // y tipo son catálogos distintos (uno por dominio), vehículo se comparte.
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [filterExpenseCategory, setFilterExpenseCategory] = useState('all');
  const [filterEntryType, setFilterEntryType] = useState('all');

  // FE-11: la pestaña de viajes reutiliza los MISMOS filtros de la página
  // (rango de fechas y conductor), pero los manda al servidor en vez de
  // filtrar en cliente: `from`/`to` seleccionan por solape con la ventana del
  // viaje, que es la pregunta correcta para un histórico — un viaje que
  // empezó el día anterior y terminó dentro del rango pertenece al rango.
  const tripsFilter = useMemo(() => ({
    from: filterFrom ? new Date(`${filterFrom}T00:00:00`).toISOString() : undefined,
    to: filterTo ? new Date(`${filterTo}T23:59:59`).toISOString() : undefined,
    driverId: filterDriver !== 'all' ? filterDriver : undefined,
  }), [filterFrom, filterTo, filterDriver]);

  const { trips, isLoading: tripsLoading, error: tripsError } = useTrips(tripsFilter);
  const selectedTrip = trips.find(t => t.id === selectedTripId) ?? null;

  // issue #124: mismo criterio que `tripsFilter` — periodo y conductor son de
  // la barra compartida, categoría/tipo/vehículo son propios de esta pestaña.
  // El servidor filtra (§7 del contrato): no se vuelve a acotar por rol aquí.
  const expensesFilter = useMemo(() => ({
    from: filterFrom ? new Date(`${filterFrom}T00:00:00`).toISOString() : undefined,
    to: filterTo ? new Date(`${filterTo}T23:59:59`).toISOString() : undefined,
    driverId: filterDriver !== 'all' ? filterDriver : undefined,
    vehicleId: filterVehicle !== 'all' ? filterVehicle : undefined,
    categoryId: filterExpenseCategory !== 'all' ? filterExpenseCategory : undefined,
  }), [filterFrom, filterTo, filterDriver, filterVehicle, filterExpenseCategory]);
  const { expenses, isLoading: expensesLoading, error: expensesError } = useExpenses(expensesFilter);
  const { categories: expenseCategories } = useExpenseCategories();

  const logbookFilter = useMemo(() => ({
    from: filterFrom ? new Date(`${filterFrom}T00:00:00`).toISOString() : undefined,
    to: filterTo ? new Date(`${filterTo}T23:59:59`).toISOString() : undefined,
    driverId: filterDriver !== 'all' ? filterDriver : undefined,
    vehicleId: filterVehicle !== 'all' ? filterVehicle : undefined,
    entryTypeId: filterEntryType !== 'all' ? filterEntryType : undefined,
  }), [filterFrom, filterTo, filterDriver, filterVehicle, filterEntryType]);
  const { entries: logbookEntries, isLoading: logbookLoading, error: logbookError } = useLogbookEntries(logbookFilter);
  const { types: logbookTypes } = useLogbookTypes();

  const handleExportExpenses = () => exportToCsv('gastos', expenses.map(e => ({
    fecha: format(parseISO(e.incurredAt), 'yyyy-MM-dd HH:mm'),
    categoria: e.categoryLabel,
    importe: e.amount,
    moneda: e.currency,
    vehiculo: e.vehiclePlate ?? '',
    conductor: e.driverName ?? '',
    nota: e.notes ?? '',
  })));

  const handleExportLogbook = () => exportToCsv('novedades', logbookEntries.map(e => ({
    fecha: format(parseISO(e.occurredAt), 'yyyy-MM-dd HH:mm'),
    tipo: e.entryTypeLabel,
    vehiculo: e.vehiclePlate ?? '',
    conductor: e.driverName ?? '',
    relato: e.description,
  })));

  // Filters logic
  const filteredAlerts = alerts.filter(a => {
    if (filterDriver !== 'all' && a.driverId !== filterDriver) return false;
    if (filterSeverity !== 'all' && a.severity !== filterSeverity) return false;
    if (filterFrom && a.timestamp < filterFrom) return false;
    if (filterTo && a.timestamp > filterTo + 'T23:59:59') return false;
    return true;
  });

  // Chart 1: Alerts Density per Category
  const categoryDataMap: Record<string, number> = {};
  filteredAlerts.forEach(a => {
    const catName = CATEGORY_LABELS[a.category] || a.category;
    categoryDataMap[catName] = (categoryDataMap[catName] || 0) + 1;
  });
  const categoryChartData = Object.entries(categoryDataMap).map(([name, count]) => ({ name, count }));

  // Chart 2: Fleet Status Distribution (los 5 estados reales de VehicleStatus)
  const fleetStatusData = (Object.keys(VEHICLE_STATUS_LABELS) as VehicleStatus[]).map(status => ({
    name: VEHICLE_STATUS_LABELS[status],
    value: vehicles.filter(v => v.status === status).length,
    color: VEHICLE_STATUS_COLORS[status],
  }));

  // Chart 3: Driver Risk Distribution — score sigue siendo opcional (FE-16);
  // los conductores sin score se cuentan aparte, nunca se les inventa un nivel.
  const riskLevels: RiskLevel[] = ['low', 'medium', 'high'];
  const driverRiskData = riskLevels.map(level => ({
    name: RISK_LABELS[level],
    value: drivers.filter(d => d.score?.level === level).length,
    color: RISK_COLORS[level],
  }));
  const driversPendingScore = drivers.filter(d => !d.score).length;

  // KPI: tiempo promedio de resolución — calculado de alertas cerradas
  // reales (closedAt - timestamp), ya no un valor fijo inventado.
  const closedWithDuration = filteredAlerts.filter(a => a.status === 'closed' && a.closedAt);
  const avgResolutionMinutes = closedWithDuration.length > 0
    ? closedWithDuration.reduce((acc, a) => acc + (new Date(a.closedAt!).getTime() - new Date(a.timestamp).getTime()), 0) / closedWithDuration.length / 60000
    : null;

  const selectStyle = {
    padding: '0.5rem 0.75rem', background: 'var(--bg-raised)', color: 'var(--text-primary)',
    border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)', fontSize: '13px', cursor: 'pointer', outline: 'none',
  };
  const inputStyle = { ...selectStyle, colorScheme: 'dark' as const };

  const tabs = [
    { id: 'dashboard' as ReportTab, label: 'Dashboard Ejecutivo', icon: <BarChart3 size={15} /> },
    { id: 'trips'     as ReportTab, label: 'Historial de Recorridos', icon: <Route size={15} /> },
    { id: 'events'    as ReportTab, label: 'Historial de Eventos', icon: <Clock size={15} /> },
    { id: 'expenses'  as ReportTab, label: 'Gastos y Novedades', icon: <Receipt size={15} /> },
    { id: 'video'     as ReportTab, label: 'Video Histórico', icon: <Video size={15} /> },
  ];

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="reports-page__header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <FileBarChart2 size={22} style={{ color: 'var(--accent)' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: 'var(--text-primary)' }}>Reportes & Analítica Ejecutiva</h1>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>Métricas dinámicas, densidad de alertas y trazabilidad de recorridos</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="reports-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            id={`reports-tab-${t.id}`}
            className={`reports-tab ${tab === t.id ? 'reports-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="dashboard-filters" style={{ background: 'var(--bg-surface)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', marginBottom: '0' }}>
        <div className="dashboard-filters__icon">
          <Filter size={14} />
          <span>Filtros:</span>
        </div>
        <select style={selectStyle} value={filterDriver} onChange={e => setFilterDriver(e.target.value)} id="reports-filter-driver">
          <option value="all">Todos los conductores</option>
          {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {tab === 'events' && (
          <select style={selectStyle} value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} id="reports-filter-severity">
            <option value="all">Todas las severidades</option>
            <option value="critical">Crítica</option>
            <option value="medium">Media</option>
            <option value="low">Baja</option>
          </select>
        )}
        {tab === 'expenses' && (
          <>
            <select style={selectStyle} value={filterVehicle} onChange={e => setFilterVehicle(e.target.value)} id="reports-filter-vehicle">
              <option value="all">Todos los vehículos</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate}</option>)}
            </select>
            <select style={selectStyle} value={filterExpenseCategory} onChange={e => setFilterExpenseCategory(e.target.value)} id="reports-filter-expense-category">
              <option value="all">Todas las categorías de gasto</option>
              {expenseCategories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select style={selectStyle} value={filterEntryType} onChange={e => setFilterEntryType(e.target.value)} id="reports-filter-entry-type">
              <option value="all">Todos los tipos de novedad</option>
              {logbookTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Desde</label>
          <input id="reports-filter-from" type="date" style={inputStyle} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Hasta</label>
          <input id="reports-filter-to" type="date" style={inputStyle} value={filterTo} onChange={e => setFilterTo(e.target.value)} />
        </div>
        {(filterDriver !== 'all' || filterFrom || filterTo || filterSeverity !== 'all' || filterVehicle !== 'all' || filterExpenseCategory !== 'all' || filterEntryType !== 'all') && (
          <button style={{ ...selectStyle, color: '#EF4444', borderColor: '#EF444444', background: 'rgba(239,68,68,0.08)', cursor: 'pointer' }}
            onClick={() => { setFilterDriver('all'); setFilterFrom(''); setFilterTo(''); setFilterSeverity('all'); setFilterVehicle('all'); setFilterExpenseCategory('all'); setFilterEntryType('all'); }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Spinner size="lg" /></div>
      ) : (
        <>
          {/* Tab: Dashboard Ejecutivo */}
          {tab === 'dashboard' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {/* Executive KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.12)', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShieldAlert size={22} />
                  </div>
                  <div>
                    <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>{filteredAlerts.length}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Alertas Totales</span>
                  </div>
                </div>

                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(59,130,246,0.12)', color: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Route size={22} />
                  </div>
                  <div>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-muted)', display: 'block' }}>No disponible</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Distancia Recorrida — requiere distancia por periodo (BE-30 + track.list de BE-23)</span>
                  </div>
                </div>

                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', background: 'rgba(16,185,129,0.12)', color: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={22} />
                  </div>
                  <div>
                    <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'block' }}>
                      {avgResolutionMinutes !== null ? `~${avgResolutionMinutes.toFixed(1)} min` : 'Sin datos'}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Tiempo Prom. Resolución</span>
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.25rem' }}>

                {/* Bar Chart: Density by Category */}
                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Densidad de Alertas por Categoría</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={categoryChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} interval={0} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                      <Tooltip contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)' }} />
                      <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Cantidad" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Pie/Donut Chart: Fleet Status Distribution */}
                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Distribución de Estado de Flota</h3>
                  <div style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={fleetStatusData}
                          cx="50%" cy="50%"
                          innerRadius={55} outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {fleetStatusData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px',
                            color: 'var(--text-primary)'
                          }}
                          labelStyle={{ color: 'var(--text-primary)' }}
                          itemStyle={{ color: 'var(--text-primary)' }}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: '11px', color: 'var(--text-primary)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center label — explicit color to fix light-mode contrast bug */}
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center', pointerEvents: 'none'
                    }}>
                      <p style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                        {fleetStatusData.reduce((a, b) => a + b.value, 0)}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)' }}>Total Flota</p>
                    </div>
                  </div>
                </div>

                {/* Pie/Donut Chart: Driver Risk Distribution */}
                <div style={{ background: 'var(--bg-surface)', padding: '1.25rem', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Distribución de Riesgo de Conductores</h3>
                  <div style={{ position: 'relative' }}>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={driverRiskData}
                          cx="50%" cy="50%"
                          innerRadius={55} outerRadius={85}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {driverRiskData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)' }}
                          labelStyle={{ color: 'var(--text-primary)' }}
                          itemStyle={{ color: 'var(--text-primary)' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px', color: 'var(--text-primary)' }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center', pointerEvents: 'none'
                    }}>
                      <p style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                        {driverRiskData.reduce((a, b) => a + b.value, 0)}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)' }}>Con Score</p>
                    </div>
                  </div>
                  {driversPendingScore > 0 && (
                    <p style={{ margin: '0.75rem 0 0', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                      {driversPendingScore} conductor{driversPendingScore !== 1 ? 'es' : ''} con score «Pendiente» — no se incluye{driversPendingScore !== 1 ? 'n' : ''} arriba.
                    </p>
                  )}
                </div>

              </div>
            </div>
          )}

          {/* Content: Events */}
          {tab === 'events' && (
            <div className="reports-table-wrapper">
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                {filteredAlerts.length} evento{filteredAlerts.length !== 1 ? 's' : ''} encontrado{filteredAlerts.length !== 1 ? 's' : ''}
              </p>
              <div className="reports-table">
                <div className="reports-table__head">
                  <span>Conductor</span><span>Categoría</span><span>Severidad</span><span>Vehículo</span><span>Fecha / Hora</span><span>Estado</span>
                </div>
                {filteredAlerts.length === 0 ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No hay eventos que coincidan con los filtros aplicados.
                  </div>
                ) : filteredAlerts.map(a => (
                  <div key={a.id} className="reports-table__row">
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.driverName ?? '—'}</span>
                    <span>{CATEGORY_LABELS[a.category] ?? a.category}</span>
                    <span>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, background: `${SEVERITY_COLORS[a.severity]}18`, color: SEVERITY_COLORS[a.severity], border: `1px solid ${SEVERITY_COLORS[a.severity]}44` }}>
                        {SEVERITY_LABELS[a.severity]}
                      </span>
                    </span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{a.vehiclePlate}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {format(parseISO(a.timestamp), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                    </span>
                    <span>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, background: a.status === 'active' ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)', color: a.status === 'active' ? '#EF4444' : '#10B981', border: `1px solid ${a.status === 'active' ? '#EF444444' : '#10B98144'}` }}>
                        {a.status === 'active' ? 'Activo' : 'Resuelto'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content: Trips — FE-11, ya contra GET /trips (solo lectura: esta
              página informa, no opera; programar y corregir viven en Viajes) */}
          {tab === 'trips' && (
            tripsError ? (
              <UnavailablePanel
                title="No se pudieron cargar los recorridos"
                reason={tripsError.message}
              />
            ) : tripsLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Spinner /></div>
            ) : trips.length === 0 ? (
              <UnavailablePanel
                title="No hay recorridos en el periodo seleccionado"
                reason="Ajusta el rango de fechas o el conductor. El filtro selecciona los viajes que se solapan con el rango, no solo los que caben enteros dentro de él."
              />
            ) : (
              <TripsTable trips={trips} onVerDetalle={t => setSelectedTripId(t.id)} />
            )
          )}

          {/* Content: Gastos y Novedades — FE-24 (issue #124). Agrega
              /expenses (FE-12) y /logbook-entries (FE-13); ninguno de los dos
              es nuevo, solo faltaba un panel de análisis sobre ellos aquí. */}
          {tab === 'expenses' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div className="reports-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                    {expenses.length} gasto{expenses.length !== 1 ? 's' : ''} · {formatearCOP(String(expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0)))}
                  </p>
                  <Button size="sm" variant="secondary" leftIcon={<Download size={14} />} disabled={expenses.length === 0} onClick={handleExportExpenses}>
                    Exportar CSV
                  </Button>
                </div>
                {expensesError ? (
                  <UnavailablePanel title="No se pudieron cargar los gastos" reason={expensesError.message} />
                ) : expensesLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Spinner /></div>
                ) : (
                  <div className="reports-table">
                    <div className="reports-table__head">
                      <span>Fecha</span><span>Categoría</span><span>Importe</span><span>Vehículo</span><span>Conductor</span><span>Nota</span>
                    </div>
                    {expenses.length === 0 ? (
                      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        No hay gastos que coincidan con los filtros aplicados.
                      </div>
                    ) : expenses.map(e => (
                      <div key={e.id} className="reports-table__row">
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {format(parseISO(e.incurredAt), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                        </span>
                        <span>{e.categoryLabel}</span>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{formatearCOP(e.amount)}</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{e.vehiclePlate ?? '—'}</span>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{e.driverName ?? '—'}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{e.notes ?? '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="reports-table-wrapper">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                    {logbookEntries.length} novedad{logbookEntries.length !== 1 ? 'es' : ''}
                  </p>
                  <Button size="sm" variant="secondary" leftIcon={<Download size={14} />} disabled={logbookEntries.length === 0} onClick={handleExportLogbook}>
                    Exportar CSV
                  </Button>
                </div>
                {logbookError ? (
                  <UnavailablePanel title="No se pudieron cargar las novedades" reason={logbookError.message} />
                ) : logbookLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Spinner /></div>
                ) : (
                  <div className="reports-table">
                    <div className="reports-table__head" style={{ gridTemplateColumns: '1.3fr 1fr 1fr 1.3fr 2fr' }}>
                      <span>Fecha</span><span>Tipo</span><span>Vehículo</span><span>Conductor</span><span>Relato</span>
                    </div>
                    {logbookEntries.length === 0 ? (
                      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        No hay novedades que coincidan con los filtros aplicados.
                      </div>
                    ) : logbookEntries.map(e => (
                      <div key={e.id} className="reports-table__row" style={{ gridTemplateColumns: '1.3fr 1fr 1fr 1.3fr 2fr' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                          {format(parseISO(e.occurredAt), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                        </span>
                        <span>{e.entryTypeLabel}</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{e.vehiclePlate ?? '—'}</span>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{e.driverName ?? '—'}</span>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{e.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content: Video — sin ningún contrato de backend asignado */}
          {tab === 'video' && (
            <UnavailablePanel
              title="Video histórico no disponible todavía"
              reason="No existe ningún endpoint ni épica de backend para clips de video grabados. La cámara en vivo es FE-14 (bloqueada por BE-14); el histórico ni siquiera tiene contrato definido."
            />
          )}
        </>
      )}

      <TripDetailModal
        isOpen={selectedTrip !== null}
        onClose={() => setSelectedTripId(null)}
        trip={selectedTrip}
      />
    </div>
  );
}
